import {HttpClient, HttpHeaders} from '@angular/common/http';
import { Service, inject } from '@angular/core';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {Entity, EntityKey} from '../entity/entity';
import {EntityLineHeader} from '../entity/entity-line-header';
import {EntitySaveResponse} from '../entity/entity-save-response';
import {PartSearchResult} from '../products/part-search-result';
import {GoogleService} from './google-service';
import { ProjectProductType } from '../enums/project-product-type';

@Service()
export class QuoteService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);

  private urlBase = environment.serviceUrl;

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }

  public save(quote: Entity): Observable<EntitySaveResponse> {
    const url = this.urlBase + 'quotes/save';
    const body = JSON.stringify(quote);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  private getPostHeaders(): HttpHeaders {
    return this.getRequestHeaders().set("Content-Type", "application/json");
  }

  public getAssociatedOrders(quoteId: string, revision: number): Observable<EntityKey[]> {
    const url = this.urlBase + 'quotes/associatedOrders/' + quoteId + '/' + revision;
    return this.http.get<EntityKey[]>(url, {headers: this.getRequestHeaders()});
  }

  public runMultiplier(quote: Entity): Observable<EntitySaveResponse> {
    const url = this.urlBase + 'quotes/multiplier';
    const body = JSON.stringify(quote);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  public createPartsQuote(parts: PartSearchResult, brandID: string, projectProductType: ProjectProductType): Observable<Entity> {
    const url = this.urlBase + 'quotes/createPartsQuote';
    const params:  { [key: string]: string } = {};
    params['brandID'] = brandID;
    params['projectProductType'] = projectProductType;
    const body = JSON.stringify(parts);
    return this.http.post<Entity>(url, body, { headers: this.getPostHeaders(), params: params });
  }

  public copy(key: EntityKey): Observable<EntitySaveResponse> {
    const url = this.urlBase + 'quotes/copy';
    const body = JSON.stringify(key);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  public copyLines(key: EntityKey, lines: EntityLineHeader[]): Observable<EntitySaveResponse> {
    const url = this.urlBase + 'quotes/copyLines/' + key.entityId + '/' + key.entityRevision;
    const body = JSON.stringify(lines);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  public createAndCopyLines(lines: EntityLineHeader[]): Observable<EntitySaveResponse> {
    const url = this.urlBase + 'quotes/createAndCopyLines';
    const body = JSON.stringify(lines);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  public placeOrder(quote: Entity): Observable<EntitySaveResponse> {
    const url = this.urlBase + 'quotes/placeOrder';
    const body = JSON.stringify(quote);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  public cancelPlaceOrder(key: EntityKey): Observable<EntitySaveResponse> {
    const url = this.urlBase + 'quotes/cancelPlaceOrder';
    const body = JSON.stringify(key);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  public approveQuote(key: EntityKey): Observable<EntitySaveResponse> {
    const url = this.urlBase + 'quotes/approveQuote';
    const body = JSON.stringify(key);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

  isRepriceNeeded(key: EntityKey): Observable<string> {
    const url = this.urlBase + 'quotes/repriceNeeded/' + key.entityId + '/' + key.entityRevision;
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  reprice(key: EntityKey): Observable<Entity> {
    const url = this.urlBase + 'quotes/reprice';
    const body = JSON.stringify(key);
    return this.http.post<Entity>(url, body, {headers: this.getPostHeaders()});
  }

  assignNewOpportunity(quote: Entity): Observable<EntitySaveResponse> {
    const url = this.urlBase + 'quotes/assignNewOpportunity';
    const body = JSON.stringify(quote);
    return this.http.post<EntitySaveResponse>(url, body, {headers: this.getPostHeaders()});
  }

}
